import React, { useState } from 'react';
import { Formik, Field } from 'formik';
import * as Yup from 'yup';
import { Form, Row, Col, Button } from 'react-bootstrap';
import { toast } from 'react-toastify';
import MoneyField from '~/Components/Form/MoneyField';
import ModalConfirmacao from '~/Components/ModalConfirmacao';
import { extrairMensagemErro } from '~/Utils/Helpers';
import { cancelarPropostaMista } from '~/Services/Service';

const TIPOS_CANCELAMENTO = [
  { value: 'C', label: 'Cancelamento' },
  { value: 'R', label: 'Renúncia' },
  { value: 'I', label: 'Interno' },
];

const TIPOS_PAGAMENTO = [
  { value: 'C', label: 'Carteira' },
  { value: 'D', label: 'Depósito' },
];

const TIPOS_CONTA = [
  { value: '1', label: 'Corrente' },
  { value: '2', label: 'Poupança' },
];

const obterTipoLabel = (tipo) => {
  const item = TIPOS_CANCELAMENTO.find((t) => t.value === tipo);
  return item ? item.label : '-';
};

const exigeDevolucao = (tipo) => tipo === 'C' || tipo === 'R';
const exigeDadosBancarios = (tipo, pgTipo) =>
  exigeDevolucao(tipo) && pgTipo === 'D';

const schemaCancelamento = Yup.object().shape({
  tipo_canc: Yup.string()
    .required('Tipo de cancelamento é obrigatório')
    .oneOf(['C', 'R', 'I'], 'Tipo inválido'),
  devolucao: Yup.number()
    .transform((v, o) => (o === '' || o == null ? 0 : v))
    .when('tipo_canc', {
      is: (v) => exigeDevolucao(v),
      then: (s) => s.min(0, 'Devolução não pode ser negativa'),
      otherwise: (s) => s.notRequired(),
    }),
  pg_tipo: Yup.string().when('tipo_canc', {
    is: (v) => exigeDevolucao(v),
    then: (s) =>
      s
        .required('Tipo de pagamento é obrigatório')
        .oneOf(['C', 'D'], 'Tipo de pagamento inválido'),
    otherwise: (s) => s.notRequired(),
  }),
  pg_banco: Yup.string().when(['tipo_canc', 'pg_tipo'], {
    is: (tipo, pg) => exigeDadosBancarios(tipo, pg),
    then: (s) => s.required('Banco obrigatório').max(3, 'Banco até 3 dígitos'),
    otherwise: (s) => s.notRequired(),
  }),
  pg_agencia: Yup.string().when(['tipo_canc', 'pg_tipo'], {
    is: (tipo, pg) => exigeDadosBancarios(tipo, pg),
    then: (s) =>
      s.required('Agência obrigatória').max(6, 'Agência até 6 dígitos'),
    otherwise: (s) => s.notRequired(),
  }),
  pg_dig_agencia: Yup.string().when(['tipo_canc', 'pg_tipo'], {
    is: (tipo, pg) => exigeDadosBancarios(tipo, pg),
    then: (s) => s.max(2, 'Dígito da agência até 2 caracteres'),
    otherwise: (s) => s.notRequired(),
  }),
  pg_conta: Yup.string().when(['tipo_canc', 'pg_tipo'], {
    is: (tipo, pg) => exigeDadosBancarios(tipo, pg),
    then: (s) =>
      s.required('Conta obrigatória').max(12, 'Conta até 12 dígitos'),
    otherwise: (s) => s.notRequired(),
  }),
  pg_dig_conta: Yup.string().when(['tipo_canc', 'pg_tipo'], {
    is: (tipo, pg) => exigeDadosBancarios(tipo, pg),
    then: (s) => s.max(2, 'Dígito da conta até 2 caracteres'),
    otherwise: (s) => s.notRequired(),
  }),
  pg_tipo_conta: Yup.string().when(['tipo_canc', 'pg_tipo'], {
    is: (tipo, pg) => exigeDadosBancarios(tipo, pg),
    then: (s) =>
      s
        .required('Tipo de conta obrigatório')
        .oneOf(['1', '2'], 'Tipo de conta inválido'),
    otherwise: (s) => s.notRequired(),
  }),
});

const valoresIniciais = {
  tipo_canc: '',
  devolucao: 0,
  pg_tipo: '',
  pg_banco: '',
  pg_agencia: '',
  pg_dig_agencia: '',
  pg_conta: '',
  pg_dig_conta: '',
  pg_tipo_conta: '',
};

const FormCancelamento = ({ proposta, codigoGe, geLabel, onCancelado }) => {
  const [showConfirm, setShowConfirm] = useState(false);
  const [valoresPendentes, setValoresPendentes] = useState(null);
  const [enviando, setEnviando] = useState(false);

  const montarPayload = (values) => {
    const tipo = values.tipo_canc;
    const devolucao = exigeDevolucao(tipo) ? Number(values.devolucao) || 0 : 0;
    const pgTipo = exigeDevolucao(tipo) ? values.pg_tipo : '';
    const banco = exigeDadosBancarios(tipo, pgTipo) ? values.pg_banco : '';
    const agencia = exigeDadosBancarios(tipo, pgTipo) ? values.pg_agencia : '';
    const digAgencia = exigeDadosBancarios(tipo, pgTipo)
      ? values.pg_dig_agencia
      : '';
    const conta = exigeDadosBancarios(tipo, pgTipo) ? values.pg_conta : '';
    const digConta = exigeDadosBancarios(tipo, pgTipo) ? values.pg_dig_conta : '';
    const tipoConta = exigeDadosBancarios(tipo, pgTipo)
      ? values.pg_tipo_conta
      : '';
    return {
      codigo_proposta: proposta.proposta,
      codigo_ge: codigoGe,
      tipo_canc: tipo,
      devolucao,
      // Seleção de cheques para baixa não faz parte do MVP (ANALISE Risco 3).
      // Backend exige o campo (validações R6/R7/R8) — enviar 0 para evitar bloqueio.
      valor_cheques: 0,
      pg_tipo: pgTipo,
      pg_banco: banco,
      pg_agencia: agencia,
      pg_dig_agencia: digAgencia,
      pg_conta: conta,
      pg_dig_conta: digConta,
      pg_tipo_conta: tipoConta,
    };
  };

  const handleAbrirConfirmacao = (values) => {
    if (!codigoGe) {
      toast.error('Grupo Empresa não selecionado.');
      return;
    }
    setValoresPendentes(values);
    setShowConfirm(true);
  };

  const handleConfirmacao = async (confirmado) => {
    setShowConfirm(false);
    if (!confirmado || !valoresPendentes) {
      setValoresPendentes(null);
      return;
    }
    const payload = montarPayload(valoresPendentes);
    setEnviando(true);
    try {
      const resp = await cancelarPropostaMista(payload);
      const sequencia = resp && resp.sequencia ? resp.sequencia : null;
      onCancelado(proposta.proposta, sequencia);
    } catch (err) {
      toast.error(extrairMensagemErro(err));
    } finally {
      setEnviando(false);
      setValoresPendentes(null);
    }
  };

  return (
    <>
      <Formik
        initialValues={valoresIniciais}
        validationSchema={schemaCancelamento}
        onSubmit={handleAbrirConfirmacao}
      >
        {({
          values,
          errors,
          touched,
          setFieldValue,
          setFieldTouched,
          handleSubmit,
        }) => {
          const mostrarDevolucao = exigeDevolucao(values.tipo_canc);
          const mostrarBanco = exigeDadosBancarios(values.tipo_canc, values.pg_tipo);
          return (
            <Form noValidate onSubmit={handleSubmit}>
              <Form.Group as={Row} className="mb-3">
                <Form.Label column xs={12} md={3} className="small">
                  Tipo de Cancelamento
                </Form.Label>
                <Col xs={12} md={9}>
                  {TIPOS_CANCELAMENTO.map((opt) => (
                    <Form.Check
                      inline
                      key={`tipo-canc-${opt.value}`}
                      type="radio"
                      id={`tipo-canc-${opt.value}`}
                      name="tipo_canc"
                      label={opt.label}
                      value={opt.value}
                      checked={values.tipo_canc === opt.value}
                      onChange={() => {
                        setFieldValue('tipo_canc', opt.value);
                        if (!exigeDevolucao(opt.value)) {
                          setFieldValue('devolucao', 0);
                          setFieldValue('pg_tipo', '');
                          setFieldValue('pg_banco', '');
                          setFieldValue('pg_agencia', '');
                          setFieldValue('pg_dig_agencia', '');
                          setFieldValue('pg_conta', '');
                          setFieldValue('pg_dig_conta', '');
                          setFieldValue('pg_tipo_conta', '');
                        }
                      }}
                      isInvalid={!!errors.tipo_canc && touched.tipo_canc}
                    />
                  ))}
                  {errors.tipo_canc && touched.tipo_canc && (
                    <div className="text-danger small mt-1">{errors.tipo_canc}</div>
                  )}
                </Col>
              </Form.Group>

              {mostrarDevolucao && (
                <>
                  <Row>
                    <Col xs={12} md={4}>
                      <Form.Group className="mb-2">
                        <Form.Label className="small mb-1">Devolução</Form.Label>
                        <Field
                          name="devolucao"
                          component={MoneyField}
                          className={`form-control form-control-sm ${
                            errors.devolucao && touched.devolucao
                              ? 'is-invalid'
                              : ''
                          }`}
                        />
                        {errors.devolucao && touched.devolucao && (
                          <div className="text-danger small mt-1">
                            {errors.devolucao}
                          </div>
                        )}
                      </Form.Group>
                    </Col>
                    <Col xs={12} md={4}>
                      <Form.Group className="mb-2">
                        <Form.Label className="small mb-1">
                          Tipo de Pagamento
                        </Form.Label>
                        <div>
                          {TIPOS_PAGAMENTO.map((opt) => (
                            <Form.Check
                              inline
                              key={`pg-tipo-${opt.value}`}
                              type="radio"
                              id={`pg-tipo-${opt.value}`}
                              name="pg_tipo"
                              label={opt.label}
                              value={opt.value}
                              checked={values.pg_tipo === opt.value}
                              onChange={() => {
                                setFieldValue('pg_tipo', opt.value);
                                if (opt.value !== 'D') {
                                  setFieldValue('pg_banco', '');
                                  setFieldValue('pg_agencia', '');
                                  setFieldValue('pg_dig_agencia', '');
                                  setFieldValue('pg_conta', '');
                                  setFieldValue('pg_dig_conta', '');
                                  setFieldValue('pg_tipo_conta', '');
                                }
                              }}
                            />
                          ))}
                        </div>
                        {errors.pg_tipo && touched.pg_tipo && (
                          <div className="text-danger small mt-1">
                            {errors.pg_tipo}
                          </div>
                        )}
                      </Form.Group>
                    </Col>
                  </Row>

                  {mostrarBanco && (
                    <Row>
                      <Col xs={6} md={2}>
                        <Form.Group className="mb-2">
                          <Form.Label className="small mb-1">Banco</Form.Label>
                          <Form.Control
                            size="sm"
                            value={values.pg_banco}
                            onChange={(e) =>
                              setFieldValue('pg_banco', e.target.value)
                            }
                            onBlur={() => setFieldTouched('pg_banco', true)}
                            maxLength={3}
                            isInvalid={!!errors.pg_banco && touched.pg_banco}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.pg_banco}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col xs={6} md={3}>
                        <Form.Group className="mb-2">
                          <Form.Label className="small mb-1">Agência</Form.Label>
                          <Form.Control
                            size="sm"
                            value={values.pg_agencia}
                            onChange={(e) =>
                              setFieldValue('pg_agencia', e.target.value)
                            }
                            onBlur={() => setFieldTouched('pg_agencia', true)}
                            maxLength={6}
                            isInvalid={!!errors.pg_agencia && touched.pg_agencia}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.pg_agencia}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col xs={6} md={1}>
                        <Form.Group className="mb-2">
                          <Form.Label className="small mb-1">Díg.</Form.Label>
                          <Form.Control
                            size="sm"
                            value={values.pg_dig_agencia}
                            onChange={(e) =>
                              setFieldValue('pg_dig_agencia', e.target.value)
                            }
                            onBlur={() =>
                              setFieldTouched('pg_dig_agencia', true)
                            }
                            maxLength={2}
                            isInvalid={
                              !!errors.pg_dig_agencia && touched.pg_dig_agencia
                            }
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.pg_dig_agencia}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col xs={6} md={3}>
                        <Form.Group className="mb-2">
                          <Form.Label className="small mb-1">Conta</Form.Label>
                          <Form.Control
                            size="sm"
                            value={values.pg_conta}
                            onChange={(e) =>
                              setFieldValue('pg_conta', e.target.value)
                            }
                            onBlur={() => setFieldTouched('pg_conta', true)}
                            maxLength={12}
                            isInvalid={!!errors.pg_conta && touched.pg_conta}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.pg_conta}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col xs={6} md={1}>
                        <Form.Group className="mb-2">
                          <Form.Label className="small mb-1">Díg.</Form.Label>
                          <Form.Control
                            size="sm"
                            value={values.pg_dig_conta}
                            onChange={(e) =>
                              setFieldValue('pg_dig_conta', e.target.value)
                            }
                            onBlur={() => setFieldTouched('pg_dig_conta', true)}
                            maxLength={2}
                            isInvalid={!!errors.pg_dig_conta && touched.pg_dig_conta}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.pg_dig_conta}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col xs={12} md={2}>
                        <Form.Group className="mb-2">
                          <Form.Label className="small mb-1">Tipo Conta</Form.Label>
                          <div>
                            {TIPOS_CONTA.map((opt) => (
                              <Form.Check
                                inline
                                key={`tipo-conta-${opt.value}`}
                                type="radio"
                                id={`tipo-conta-${opt.value}`}
                                name="pg_tipo_conta"
                                label={opt.label}
                                value={opt.value}
                                checked={values.pg_tipo_conta === opt.value}
                                onChange={() => {
                                  setFieldValue('pg_tipo_conta', opt.value);
                                  setFieldTouched('pg_tipo_conta', true);
                                }}
                              />
                            ))}
                          </div>
                          {errors.pg_tipo_conta && touched.pg_tipo_conta && (
                            <div className="text-danger small mt-1">
                              {errors.pg_tipo_conta}
                            </div>
                          )}
                        </Form.Group>
                      </Col>
                    </Row>
                  )}
                </>
              )}

              <div className="d-flex justify-content-end mt-3">
                <Button
                  type="submit"
                  variant="danger"
                  size="sm"
                  disabled={!values.tipo_canc || enviando}
                >
                  {enviando ? 'Lançando...' : 'Lançar Cancelamento'}
                </Button>
              </div>
            </Form>
          );
        }}
      </Formik>

      <ModalConfirmacao
        show={showConfirm}
        onClose={handleConfirmacao}
        title="Confirmar Cancelamento"
        message={`Confirma o cancelamento da proposta ${
          proposta ? proposta.proposta : ''
        } (${geLabel || `GE ${codigoGe || '-'}`}, ${obterTipoLabel(
          valoresPendentes ? valoresPendentes.tipo_canc : ''
        )})? Esta ação é irreversível.`}
      />
    </>
  );
};

export default FormCancelamento;
